import React from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { ApolloProvider } from 'lib/core/components/ApolloProvider'
import { JsonLd } from 'lib/shared'
import { useCategoryByPath, useProductByPath } from 'lib/modules/catalog'
import { useStoreConfig } from 'lib/core/hooks'
import { getUrlPath } from 'lib/core/resources/helpers'
import { getPageList } from 'lib/core/data/common/pageList/selectors'

interface Props {
  type?: 'PLP' | 'PDP' | 'CMS'
  title?: string
  description?: string
  keywords?: string
  extra?: [{name: string, content: string}]
}

export const MetaData = (props: Props) => {
  return (
    <ApolloProvider>
      <MetaDataComponent {...props} />
    </ApolloProvider>
  )
}

const MetaDataComponent = ({ type, title, description, keywords, extra }: Props) => {
  const { pwaUrl } = useStoreConfig()
  const { meta: categoryMetaData } = useCategoryByPath()
  const { product } = useProductByPath()
  const location = useLocation()
  const slug = getUrlPath(location)
  const pageList = useSelector(getPageList)
  const cmsPageData = pageList?.identifier[slug]
  const { headerLogo, defaultMetaData } = useStoreConfig()
  const history = useHistory()

  let metaDescription
  let metaKeywords
  let image = headerLogo
  let pageTitle

  switch (type) {
    case 'PLP':
      metaDescription = categoryMetaData?.description
      metaKeywords = categoryMetaData?.keywords
      pageTitle = categoryMetaData?.title
      break
    case 'PDP':
      metaDescription = product?.metaDescription
      metaKeywords = product?.metaKeyword
      image = product?.image.default.url ?? ''
      pageTitle = product?.metaTitle ?? product?.name
      break
    case 'CMS':
      metaDescription = cmsPageData?.meta_description
      metaKeywords = cmsPageData?.meta_keywords
      pageTitle = cmsPageData?.meta_title ?? cmsPageData?.title
      break
    default:
      metaDescription = defaultMetaData?.metaDescription
      metaKeywords = defaultMetaData?.metaKeywords
      pageTitle = defaultMetaData?.metaTitle ?? title
  }

  if (description) {
    metaDescription = description
  }

  if (keywords) {
    metaKeywords = keywords
  }

  if (title) {
    pageTitle = title
  }

  const productPath = product?.canonicalUrl ?? product?.urlKey
  const canonicalUrl = !pwaUrl ? undefined : productPath
    ? `${pwaUrl}/${productPath}`
    : `${pwaUrl}${history.location.pathname}`

  return (
    <>
      <Helmet
        titleTemplate='%s'
      >
        {pageTitle && (
          <title>
            {pageTitle}
          </title>
        )}
        {metaKeywords && (
          <meta name='keywords' content={metaKeywords} />
        )}
        {metaDescription && (
          <meta name='description' content={metaDescription} />
        )}
        {image && (
          <meta property='og:image' content={image} />
        )}
        {extra?.map((metaData) => {
          return (
            <meta key={metaData.name} name={metaData.name} content={metaData.content} />
          )
        })}
      </Helmet>

      {product && (
        <JsonLd.JsonLdProduct product={product} />
      )}

      {canonicalUrl && (
        <link rel='canonical' href={canonicalUrl} />
      )}
    </>
  )
}
